import * as React from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronDown,
  ListFilter,
  Search,
  SlidersHorizontal,
  WrapText,
  X,
} from 'lucide-react';
import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
} from '@/components/ui/BottomSheet';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';

/**
 * MobileTableShell — the Asana / Linear / Notion-style mobile chrome
 * that wraps a list of `MobileListCard`s.
 *
 * Three regions, stacked vertically:
 *
 *   1. Toolbar
 *      ┌──────────────────────────────────────────┐
 *      │ 🔍 Search…                  ⇅ Sort  ☰ Filter (3) │
 *      └──────────────────────────────────────────┘
 *
 *      Sits at the top of the list and scrolls away with it, so it
 *      never floats over the cards.
 *
 *   2. Active-filter strip (only when filters are applied)
 *      ┌──────────────────────────────────────────┐
 *      │ ⓧ Status: Active  ⓧ Kind: Raw  · Clear   │
 *      └──────────────────────────────────────────┘
 *
 *      Lets the operator drop a single filter without re-opening the
 *      full filter sheet (Asana's "active facet" pattern).
 *
 *   3. The list itself (rendered as `children`).
 *
 *   4. Optional footer:
 *      * "Showing 25 of 412 · Load more" pager (default) — replaces
 *        the desktop's tiny chevron buttons.
 *      * Floating bottom action bar when selection is active — shows
 *        the count, "Clear" affordance, and the page's bulk actions.
 *
 * The toolbar is driven by lifted state — `MobileTableShell` itself
 * is purely presentational and pushes search / sort / filter changes
 * back to the caller. This keeps the shell reusable for both the
 * DataTable mobile branch AND raw `<Table>` migrations that don't
 * use TanStack at all (BankAccountsPage, ProjectDashboard.MyTasks).
 */

export interface MobileSortOption {
  /** Stable key returned via `onSortChange`. */
  id: string;
  label: string;
}

export interface MobileFilterGroup {
  id: string;
  title: string;
  options: Array<{
    value: string;
    label: string;
    /** Optional decorative icon (Lucide component or any node). */
    icon?: React.ReactNode;
    /** Optional count badge (e.g. faceted facet count). */
    count?: number;
  }>;
  /** Currently selected option values. Multi-select unless the group
   *  exposes only one option at a time (handled by the caller). */
  selected: string[];
  onChange: (next: string[]) => void;
  /** When true, only one option can be selected at a time (status
   *  pickers, sort fallbacks). Defaults to multi-select. */
  single?: boolean;
}

export interface MobileColumnVisibilityOption {
  id: string;
  label: string;
  visible: boolean;
  onToggle: (visible: boolean) => void;
}

export interface MobileTableShellProps {
  // ── Search ───────────────────────────────────────────────────
  /** Pass `null` to hide the search input. */
  searchPlaceholder?: string | null;
  searchValue?: string;
  onSearchChange?: (value: string) => void;

  // ── Sort ─────────────────────────────────────────────────────
  sortOptions?: MobileSortOption[];
  activeSortId?: string | null;
  activeSortDir?: 'asc' | 'desc' | null;
  onSortChange?: (id: string | null, dir: 'asc' | 'desc' | null) => void;

  // ── Filters ──────────────────────────────────────────────────
  filterGroups?: MobileFilterGroup[];
  /** Optional column-visibility list. Surfaced inside the same
   *  "Options" sheet as filters / sort, never in a separate dropdown
   *  (mobile has no room for another popover). */
  columnVisibility?: MobileColumnVisibilityOption[];
  /** Optional wrap-text toggle (mirrors desktop). */
  textWrap?: { value: boolean; onChange: (next: boolean) => void };
  /** "Reset" pill clears every filter, sort, and search at once. */
  onReset?: () => void;

  // ── Selection ────────────────────────────────────────────────
  selectionCount?: number;
  /** Bulk actions shown in the floating bottom bar when
   *  `selectionCount > 0`. Caller decides what these are. */
  bulkActions?: React.ReactNode;
  onClearSelection?: () => void;

  // ── Footer / pager ───────────────────────────────────────────
  totalCount?: number;
  visibleCount?: number;
  /** When provided, renders a "Load more" pill at the bottom of the
   *  list. Calling this should append the next slice of rows to the
   *  rendered set. */
  onLoadMore?: () => void;
  hasMore?: boolean;

  // ── Right-side toolbar slot (for page-specific actions like
  //    "Post N pending" in LiveTransactions). Hidden inside the
  //    options sheet so the toolbar stays single-row. ─────
  pageActions?: React.ReactNode;

  /** The list itself. Typically a `<MobileListCardStack>`. */
  children: React.ReactNode;

  className?: string;
}

export function MobileTableShell({
  searchPlaceholder = 'Search…',
  searchValue = '',
  onSearchChange,
  sortOptions,
  activeSortId,
  activeSortDir,
  onSortChange,
  filterGroups,
  columnVisibility,
  textWrap,
  onReset,
  selectionCount = 0,
  bulkActions,
  onClearSelection,
  totalCount,
  visibleCount,
  onLoadMore,
  hasMore,
  pageActions,
  children,
  className,
}: MobileTableShellProps) {
  const [optionsOpen, setOptionsOpen] = React.useState(false);

  const activeFilterCount = React.useMemo(() => {
    if (!filterGroups) return 0;
    return filterGroups.reduce((acc, g) => acc + g.selected.length, 0);
  }, [filterGroups]);

  const hasActiveFilters = activeFilterCount > 0 || !!activeSortId || !!searchValue;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* ─────────────────── Toolbar ─────────────────── */}
      {/* Deliberately not sticky: a pinned bar floats over the cards
          as the operator scrolls and hides the text beneath it. */}
      <div className="-mx-4 border-b border-border bg-background px-4 py-2">
        <div className="flex items-center gap-2">
          {searchPlaceholder !== null && (
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-10 pl-9 pr-8 text-base"
                inputMode="search"
                type="search"
                enterKeyHint="search"
              />
              {searchValue && (
                <button
                  type="button"
                  onClick={() => onSearchChange?.('')}
                  className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}

          {/* Single "Options" pill that opens the BottomSheet. We
              deliberately collapse sort + filter + view + wrap into
              one entry point — Asana / Linear both do this because a
              second pill burns horizontal real estate that the search
              bar needs. The pill shows a count badge when any filter
              is active so the operator knows there's something to
              reset. */}
          {((filterGroups && filterGroups.length > 0) ||
            (sortOptions && sortOptions.length > 0) ||
            (columnVisibility && columnVisibility.length > 0) ||
            textWrap) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOptionsOpen(true)}
              className={cn(
                'h-10 shrink-0 gap-1.5 px-3',
                hasActiveFilters && 'border-primary text-primary',
              )}
              aria-label="Sort, filter, and view options"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {activeFilterCount > 0 ? (
                <Badge variant="default" className="h-4 rounded-full px-1.5 text-[10px]">
                  {activeFilterCount}
                </Badge>
              ) : null}
            </Button>
          )}
        </div>

        {/* Active-facet strip. Tap a chip to remove that single
            filter; the strip auto-hides when nothing is active. */}
        {(activeFilterCount > 0 || !!activeSortId || !!searchValue) && (
          <div className="-mx-1 mt-2 flex flex-wrap items-center gap-1.5">
            {searchValue && (
              <button
                type="button"
                onClick={() => onSearchChange?.('')}
                className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs text-foreground hover:bg-humus/10"
              >
                <span className="text-muted-foreground">Search:</span>
                <span className="max-w-[8rem] truncate font-medium">
                  {searchValue}
                </span>
                <X className="h-3 w-3" />
              </button>
            )}
            {filterGroups?.map((g) =>
              g.selected.map((v) => {
                const opt = g.options.find((o) => o.value === v);
                if (!opt) return null;
                return (
                  <button
                    key={`${g.id}-${v}`}
                    type="button"
                    onClick={() =>
                      g.onChange(g.selected.filter((s) => s !== v))
                    }
                    className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs text-foreground hover:bg-humus/10"
                  >
                    <span className="text-muted-foreground">{g.title}:</span>
                    <span className="max-w-[8rem] truncate font-medium">
                      {opt.label}
                    </span>
                    <X className="h-3 w-3" />
                  </button>
                );
              }),
            )}
            {activeSortId && onSortChange && (
              <button
                type="button"
                onClick={() => onSortChange(null, null)}
                className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs text-foreground hover:bg-humus/10"
              >
                {activeSortDir === 'desc' ? (
                  <ArrowDown className="h-3 w-3" />
                ) : (
                  <ArrowUp className="h-3 w-3" />
                )}
                <span className="max-w-[8rem] truncate font-medium">
                  {sortOptions?.find((s) => s.id === activeSortId)?.label ??
                    activeSortId}
                </span>
                <X className="h-3 w-3" />
              </button>
            )}
            {onReset && (
              <button
                type="button"
                onClick={onReset}
                className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              >
                Clear all
              </button>
            )}
          </div>
        )}

        {/* Optional page-action slot (Post N pending, Add item …).
            Lives inside the toolbar so it scrolls with the search bar
            and doesn't compete with the floating bulk-action bar. */}
        {pageActions && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {pageActions}
          </div>
        )}
      </div>

      {/* ─────────────────── The list itself ─────────────────── */}
      {children}

      {/* ─────────────────── Footer / pager ─────────────────── */}
      {(totalCount !== undefined || onLoadMore) && (
        <div className="flex flex-col items-center gap-2 pb-2 pt-1">
          {totalCount !== undefined && visibleCount !== undefined && (
            <p className="text-xs text-muted-foreground">
              Showing {visibleCount.toLocaleString()} of{' '}
              {totalCount.toLocaleString()}
            </p>
          )}
          {onLoadMore && hasMore && (
            <Button
              variant="outline"
              size="sm"
              onClick={onLoadMore}
              className="h-10 min-w-[10rem] gap-1.5"
            >
              <ChevronDown className="h-4 w-4" />
              Load more
            </Button>
          )}
        </div>
      )}

      {/* ─────────────────── Floating bulk-action bar ─────────────────── */}
      {/* Sits above the iOS home indicator (env safe-area-inset) so a
          thumb tap doesn't trigger the system gesture. Fades in only
          when at least one row is selected — keeps the chrome clean
          for the 95 % case of casual browsing. */}
      {selectionCount > 0 && (
        <div
          role="region"
          aria-label="Bulk actions"
          className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-2"
        >
          <div className="pointer-events-auto mx-auto flex max-w-md items-center gap-2 rounded-2xl border border-border bg-card px-3 py-2 shadow-xl">
            <Badge variant="default" className="rounded-full">
              {selectionCount}
            </Badge>
            <span className="flex-1 truncate text-xs text-muted-foreground">
              {selectionCount === 1 ? 'row' : 'rows'} selected
            </span>
            {onClearSelection && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearSelection}
                className="h-8 px-2"
              >
                Clear
              </Button>
            )}
            {bulkActions}
          </div>
        </div>
      )}

      {/* ─────────────────── Options bottom sheet ─────────────────── */}
      <BottomSheet open={optionsOpen} onOpenChange={setOptionsOpen}>
        <BottomSheetHeader
          title="Sort & filter"
        />
        <BottomSheetBody className="space-y-6">
          {sortOptions && sortOptions.length > 0 && onSortChange && (
            <Section title="Sort">
              <div className="space-y-1">
                {sortOptions.map((opt) => {
                  const active = activeSortId === opt.id;
                  return (
                    <div
                      key={opt.id}
                      className={cn(
                        'flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5',
                        active
                          ? 'border-primary bg-primary/5'
                          : 'border-border bg-card',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          // Tapping a non-active sort defaults to
                          // descending (newest / largest first is
                          // what the operator wants 9/10 times).
                          // Tapping the active sort toggles direction.
                          if (!active) onSortChange(opt.id, 'desc');
                          else
                            onSortChange(
                              opt.id,
                              activeSortDir === 'asc' ? 'desc' : 'asc',
                            );
                        }}
                        className="flex flex-1 items-center justify-between gap-2 text-left"
                      >
                        <div className="text-sm font-medium text-foreground">
                          {opt.label}
                        </div>
                        {active ? (
                          activeSortDir === 'asc' ? (
                            <ArrowUp className="h-4 w-4 text-primary" />
                          ) : (
                            <ArrowDown className="h-4 w-4 text-primary" />
                          )
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-muted-foreground/40" />
                        )}
                      </button>
                    </div>
                  );
                })}
                {activeSortId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSortChange(null, null)}
                    className="w-full justify-start"
                  >
                    <X className="h-3.5 w-3.5" />
                    Clear sort
                  </Button>
                )}
              </div>
            </Section>
          )}

          {filterGroups?.map((g) => (
            <Section key={g.id} title={g.title}>
              <div className="flex flex-wrap gap-1.5">
                {g.options.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No values.</p>
                ) : (
                  g.options.map((opt) => {
                    const active = g.selected.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          if (g.single) {
                            g.onChange(active ? [] : [opt.value]);
                          } else {
                            g.onChange(
                              active
                                ? g.selected.filter((s) => s !== opt.value)
                                : [...g.selected, opt.value],
                            );
                          }
                        }}
                        className={cn(
                          'inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors',
                          active
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-card text-foreground hover:bg-secondary',
                        )}
                      >
                        {active && <Check className="h-3 w-3" />}
                        {opt.icon}
                        <span className="font-medium">{opt.label}</span>
                        {typeof opt.count === 'number' && opt.count > 0 && (
                          <span
                            className={cn(
                              'tabular-nums text-[10px]',
                              active
                                ? 'text-primary-foreground/70'
                                : 'text-muted-foreground',
                            )}
                          >
                            {opt.count}
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </Section>
          ))}

          {textWrap && (
            <Section title="Display">
              <div className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <WrapText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      Wrap long values
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Off truncates with ellipsis.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={textWrap.value}
                  onClick={() => textWrap.onChange(!textWrap.value)}
                  className={cn(
                    'relative h-6 w-10 rounded-full transition-colors',
                    textWrap.value ? 'bg-primary' : 'bg-border',
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-0.5 h-5 w-5 rounded-full bg-card shadow transition-all',
                      textWrap.value ? 'left-[1.125rem]' : 'left-0.5',
                    )}
                  />
                </button>
              </div>
            </Section>
          )}

          {columnVisibility && columnVisibility.length > 0 && (
            <Section title="Visible fields">
              <div className="grid grid-cols-2 gap-1.5">
                {columnVisibility.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => c.onToggle(!c.visible)}
                    className={cn(
                      'inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs',
                      c.visible
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-foreground',
                    )}
                  >
                    {c.visible && <Check className="h-3 w-3" />}
                    <span className="truncate">{c.label}</span>
                  </button>
                ))}
              </div>
            </Section>
          )}
        </BottomSheetBody>
        <BottomSheetFooter>
          {onReset && hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={() => {
                onReset();
              }}
              className="w-full sm:w-auto"
            >
              <ListFilter className="h-4 w-4" />
              Reset
            </Button>
          )}
          <Button
            onClick={() => setOptionsOpen(false)}
            className="w-full sm:w-auto"
          >
            Done
          </Button>
        </BottomSheetFooter>
      </BottomSheet>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}
